import { CalendarMonthOutlined, ExpandMore } from "@mui/icons-material";
import {
  endOfMonth,
  format,
  isAfter,
  isSameDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./DateRangeSelect.css";

const parseYmd = (value) => {
  if (!value || value === "All") return null;
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const clampToToday = (date) => {
  const t = today();
  return isAfter(date, t) ? t : date;
};

const PRESETS = [
  {
    label: "Today",
    getRange: () => {
      const d = today();
      return { startDate: d, endDate: d };
    },
  },
  {
    label: "Yesterday",
    getRange: () => {
      const d = subDays(today(), 1);
      return { startDate: d, endDate: d };
    },
  },
  {
    label: "Last 7 Days",
    getRange: () => ({
      startDate: subDays(today(), 6),
      endDate: today(),
    }),
  },
  {
    label: "Last 30 Days",
    getRange: () => ({
      startDate: subDays(today(), 29),
      endDate: today(),
    }),
  },
  {
    label: "This Month",
    getRange: () => ({
      startDate: startOfMonth(today()),
      endDate: today(),
    }),
  },
  {
    label: "Last Month",
    getRange: () => {
      const last = subMonths(today(), 1);
      return {
        startDate: startOfMonth(last),
        endDate: endOfMonth(last),
      };
    },
  },
];

const defaultSelection = () => [
  {
    startDate: today(),
    endDate: today(),
    key: "selection",
  },
];

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
};

/**
 * Custom dual-calendar date range picker (Apply / Clear + quick presets).
 * onApply(start, end) → formatted YYYY-MM-DD strings
 * onClear() → parent should reset to "All"
 */
const DateRangeSelect = ({
  onApply,
  onClear,
  startDate, // applied value: "All" | "YYYY-MM-DD"
  endDate,
  placeholder = "Select Date",
}) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(defaultSelection);
  const wrapRef = useRef(null);
  const isMobile = useIsMobile(768);

  // Sync draft range when opening from applied filter values
  useEffect(() => {
    if (!open) return;
    const start = parseYmd(startDate);
    const end = parseYmd(endDate);
    if (start && end) {
      setRange([
        {
          startDate: start,
          endDate: end,
          key: "selection",
        },
      ]);
      return;
    }
    setRange(defaultSelection());
  }, [open, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);
    // lock body scroll on mobile while picker is open
    const prevOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, isMobile]);

  const activePresetLabel = PRESETS.find((p) => {
    const r = p.getRange();
    return (
      isSameDay(range[0].startDate, r.startDate) &&
      isSameDay(range[0].endDate, r.endDate)
    );
  })?.label;

  const displayLabel = (() => {
    const start = parseYmd(startDate);
    const end = parseYmd(endDate);
    if (start && end) {
      return `${format(start, "MM/dd/yyyy")} - ${format(end, "MM/dd/yyyy")}`;
    }
    return placeholder;
  })();

  const handleRangeChange = (item) => {
    const sel = item.selection;
    setRange([
      {
        startDate: clampToToday(sel.startDate),
        endDate: clampToToday(sel.endDate),
        key: "selection",
      },
    ]);
  };

  const handlePreset = (preset) => {
    const r = preset.getRange();
    setRange([
      {
        startDate: r.startDate,
        endDate: r.endDate,
        key: "selection",
      },
    ]);
  };

  const handleApply = () => {
    const start = format(range[0].startDate, "yyyy-MM-dd");
    const end = format(range[0].endDate, "yyyy-MM-dd");
    onApply?.(start, end);
    setOpen(false);
  };

  const handleClear = () => {
    setRange(defaultSelection());
    onClear?.();
    setOpen(false);
  };

  const draftLabel = `${format(range[0].startDate, "MM/dd/yyyy")} - ${format(
    range[0].endDate,
    "MM/dd/yyyy"
  )}`;

  return (
    <div className={`drs-wrapper${open ? " is-open" : ""}`} ref={wrapRef}>
      <button
        type="button"
        className="drs-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <CalendarMonthOutlined className="drs-trigger-icon" fontSize="small" />
        <span className="drs-trigger-label">{displayLabel}</span>
        <ExpandMore
          className={`drs-trigger-chevron${open ? " open" : ""}`}
          fontSize="small"
        />
      </button>

      {open && (
        <>
          {isMobile && (
            <div
              className="drs-backdrop"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
          )}
          <div className={`drs-popover${isMobile ? " is-mobile" : ""}`}>
            <div className="drs-inputs">
              <input
                type="text"
                readOnly
                className="drs-input"
                value={format(range[0].startDate, "MM/dd/yyyy")}
              />
              <span className="drs-input-sep">–</span>
              <input
                type="text"
                readOnly
                className="drs-input"
                value={format(range[0].endDate, "MM/dd/yyyy")}
              />
            </div>

            <div className="drs-calendar-wrap">
              <DateRange
                editableDateInputs={false}
                onChange={handleRangeChange}
                moveRangeOnFirstSelection={false}
                ranges={range}
                months={isMobile ? 1 : 2}
                direction={isMobile ? "vertical" : "horizontal"}
                maxDate={today()}
                showDateDisplay={false}
                showMonthAndYearPickers={true}
                rangeColors={["#b93160"]}
                className="drs-calendar"
              />
            </div>

            <div className="drs-presets">
              <span className="drs-presets-title">Quick Select</span>
              <div className="drs-presets-list">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    className={`drs-preset-btn${
                      activePresetLabel === preset.label ? " active" : ""
                    }`}
                    onClick={() => handlePreset(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="drs-footer">
              <div className="drs-footer-range">
                <CalendarMonthOutlined fontSize="small" />
                <span>{draftLabel}</span>
              </div>
              <div className="drs-footer-actions">
                <button
                  type="button"
                  className="drs-btn-clear"
                  onClick={handleClear}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="drs-btn-apply"
                  onClick={handleApply}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangeSelect;
